import { apiFetch } from './client';

// ──────────────────────────────────────────────────────────
// Shared types (mirror backend/src/carriers/service.ts)
// ──────────────────────────────────────────────────────────

export type CarrierScope = 'platform' | 'reseller' | 'customer';
export type CarrierKind = 'voice' | 'sms' | 'both';

export interface SipConfig {
  host: string;
  port?: number;
  transport?: 'udp' | 'tcp';
  username?: string;
  register?: boolean;
  expire_seconds?: number;
  from_user?: string | null;
  from_domain?: string | null;
  proxy?: string | null;
  caller_id_in_from?: boolean;
  inbound_cidrs?: string[];
  dial_string_extras?: string[];
}

export interface CarrierRow {
  id: string;
  reseller_id: string | null;
  customer_id: string | null;
  ref_number: string | null;
  scope: CarrierScope;
  name: string;
  kind: CarrierKind;
  provider: string;
  sip_config: SipConfig;
  priority: number;
  active: boolean;
  has_password: boolean;
  created_at: string;
  updated_at: string;
}

// ──────────────────────────────────────────────────────────
// Input types
// ──────────────────────────────────────────────────────────

export interface CreateCarrierBody {
  name: string;
  kind: CarrierKind;
  provider: string;
  scope: CarrierScope;
  reseller_id?: string | null;
  customer_id?: string | null;
  sip_config: SipConfig;
  priority?: number;
  /** plaintext password — backend encrypts before storing */
  password?: string;
}

export type UpdateCarrierBody = Partial<Omit<CreateCarrierBody, 'scope'>> & {
  /** Only super_admin may change scope */
  scope?: CarrierScope;
  active?: boolean;
  /**
   * password: ''    → explicit clear (wipes stored credential)
   * password: '…'   → re-encrypt and store new password
   * password absent → keep existing password unchanged
   */
  password?: string;
};

// ──────────────────────────────────────────────────────────
// Response shapes that include a sync_warning field
// ──────────────────────────────────────────────────────────

export interface CarrierMutationResponse extends CarrierRow {
  sync_warning?: string;
}

// ──────────────────────────────────────────────────────────
// API client
// ──────────────────────────────────────────────────────────

export const carriers = {
  /**
   * List carriers, optionally filtered by scope, kind, and/or free-text search.
   * Returns up to `limit` rows (backend max 200).
   */
  list: (
    p: { scope?: CarrierScope; kind?: CarrierKind; search?: string; limit?: number } = {},
  ) => {
    const q = new URLSearchParams();
    if (p.scope) q.set('scope', p.scope);
    if (p.kind) q.set('kind', p.kind);
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: CarrierRow[] }>(`/api/carriers?${q}`);
  },

  /** Fetch a single carrier row by id. */
  get: (id: string) => apiFetch<CarrierRow>(`/api/carriers/${id}`),

  /**
   * Create a new carrier. The response may include a `sync_warning` if the
   * auto-sync to FreeSWITCH partially failed (DB row is still saved).
   */
  create: (body: CreateCarrierBody) =>
    apiFetch<CarrierMutationResponse>('/api/carriers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Update an existing carrier. Same sync_warning semantics as create.
   */
  update: (id: string, body: UpdateCarrierBody) =>
    apiFetch<CarrierMutationResponse>(`/api/carriers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * Soft-delete a carrier. Returns 204 on success; the backend triggers a
   * FreeSWITCH sync to remove the gateway file.
   */
  remove: (id: string) =>
    apiFetch<void>(`/api/carriers/${id}`, { method: 'DELETE' }),

  /**
   * Test live connectivity to a carrier's gateway.
   * NOTE: The backend returns 501 (not implemented) in the current release.
   * The UI shows a "pending" toast rather than calling this endpoint.
   */
  test: (id: string) =>
    apiFetch<{ status: string; detail?: string }>(`/api/carriers/${id}/test`, {
      method: 'POST',
    }),
};
